import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import Flatpickr from "react-flatpickr";
import Modal from "../Modal";
import {
  ExclamationCircleIcon,
  TrashIcon,
  XMarkIcon,
  EllipsisVerticalIcon,
  PlusIcon,
} from "@heroicons/react/24/solid";


import {
  ProvisionalPost,
  ChannelAccount,
  ProvisionalBody,
  Channel,
} from "../../types";
import { Switch } from "@headlessui/react";
import Select from "../Select";
import ChannelAccountImage from "../ChannelAccountImage";
import Tooltip from "../Tooltip";

import PostPreview from "./PostPreview";
import { CoverUploader } from "../utils/publisher/coverUploader"
import { ValidationObject } from "../usePubValidation";

export default function PublicationManager({
  post,
  setPost,
  validationObject,
  hChannelAccounts,
  hBodies,
  variables,
  openModalForPoc,
  setOpenModalForPoc,
}: {
  post: ProvisionalPost;
  validationObject: ValidationObject;
  setPost: React.Dispatch<React.SetStateAction<ProvisionalPost>>;
  hChannelAccounts: Record<number, ChannelAccount>;
  hBodies: Record<number, ProvisionalBody>;
  variables: Record<string, string | null>;
  openModalForPoc: ProvisionalPost["post_on_channel_attributes"][0] | null;
  setOpenModalForPoc: React.Dispatch<
    React.SetStateAction<
      ProvisionalPost["post_on_channel_attributes"][0] | null
    >
  >;
}) {
  const intl = useIntl();
  const { errors, reverseMap } = validationObject;

  const oneHourFromNow = new Date();
  oneHourFromNow.setMinutes(oneHourFromNow.getMinutes() + 15);

  const togglePublish = (instantPublish: boolean) => {
    setPost((prev) => {
      return {
        ...prev,
        scheduled_at: !instantPublish
          ? prev.scheduled_at
          : oneHourFromNow.toISOString(),
        instant_publish: instantPublish,
        post_on_channels_attributes: prev.post_on_channels_attributes.map(
          (poc) => ({
            ...poc,
            forced_scheduled_at: instantPublish
              ? ""
              : poc.forced_scheduled_at || oneHourFromNow.toISOString(),
          })
        ),
      };
    });
  };

  // Sort posts with errors first
  const sortedPosts = [...post?.post_on_channels_attributes].sort((a, b) => {
    const erroredPocs = Object.keys(reverseMap);
    const aHasErrors = erroredPocs.some(
      (id) => id === a?.channel_account_id.toString()
    );
    const bHasErrors = erroredPocs.some(
      (id) => id === b?.channel_account_id.toString()
    );
    return aHasErrors === bHasErrors ? 0 : aHasErrors ? -1 : 1;
  });

  const updatePoc = (
    poc: ProvisionalPost["post_on_channels_attributes"][0],
    attributes: Record<string, any>
  ) => {
    setPost((prev) => {
      const post_on_channels_attributes = prev.post_on_channels_attributes.map(
        (prevPoc) => {
          if (
            prevPoc.channel_account_id === poc.channel_account_id &&
            prevPoc.body_provisional_id === poc.body_provisional_id
          ) {
            return { ...prevPoc, ...attributes };
          }
          return prevPoc;
        }
      );
      return { ...prev, post_on_channels_attributes };
    });
  };

  const deletePoc = (
    poc: ProvisionalPost["post_on_channels_attributes"][0]
  ) => {
    setPost((prev) => ({
      ...prev,
      post_on_channels_attributes: prev.post_on_channels_attributes.filter(
        (prevPoc) => prevPoc.channel_account_id !== poc.channel_account_id
      ),
    }));
  };

  // Le post est issu d'une proposition, on ne peut pas changer la date
  const disabledDate = post["was_proposal?"];

  return (
    <>
      <div className="flex gap-5 p-4 bg-white rounded shadow justify-between mx-1 sm:mx-0">
        <div className="flex flex-col gap-3 w-full p-2">
          <div className="">
            <h3 className="stonly-post-customization text-lg font-medium text-gray-700 w-fit">
              {intl.formatMessage({ id: "publication_manager.customization", defaultMessage: "Personnalisation des posts" })}
            </h3>
            <h4 className="text-gray-500">
              {intl.formatMessage({ id: "publication_manager.posts_ready", defaultMessage: "{count} posts prêts pour la publication" }, { count: post?.post_on_channels_attributes?.length })}
            </h4>
          </div>
          <div className="flex flex-wrap justify-between gap-5">
            {sortedPosts.map((poc, index) => {
              const channelAccount = hChannelAccounts[poc.channel_account_id];

              if (!channelAccount.id) return;
              const pocErrors =
                reverseMap[channelAccount.id]?.map(
                  (identifier) => errors[identifier]
                ) || [];
              return (
                <PostPreview
                  key={`preview-${index}`}
                  errors={pocErrors}
                  body={hBodies[poc.body_provisional_id]}
                  variables={variables}
                  mentions={poc?.channel_specific_data?.mentions || {}}
                  channelAccount={channelAccount}
                  poc={poc}
                  header={
                    <div className="flex shrink items-center justify-between p-3 text-sm text-gray-500 rounded-md bg-surface_50">
                      <div className="flex items-center h-12 gap-3 px-3 min-h-12 max-h-12">
                        <ChannelAccountImage
                          identifier={
                            channelAccount?.channel
                              ?.identifier as Channel["identifier"]
                          }
                          ca={channelAccount}
                          withOutline={true}
                          isActive={true}
                          imageSize={10}
                          logoSize={4}
                          withChannelLogo={true}
                        />

                        <div className="flex flex-col">
                          {poc?.forced_scheduled_at && (
                            <p>
                              {intl.formatMessage({ id: "publication_manager.published_on", defaultMessage: "Publié le" })}{" "}
                              {new Date(poc.forced_scheduled_at).toLocaleString(
                                intl.locale,
                                {
                                  dateStyle: "short",
                                  timeStyle: "short",
                                }
                              )}
                            </p>
                          )}
                          <p className="font-medium text-gray-700 line-clamp-2">
                            {channelAccount?.displayed_name}
                          </p>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => {
                          setOpenModalForPoc(poc);
                        }}
                        className="relative border rounded hover:bg-gray-100"
                      >
                        <Tooltip text={intl.formatMessage({ id: "publication_manager.adjust_options", defaultMessage: "Ajustez les options et l'horaire du post" })}>
                          <EllipsisVerticalIcon className="w-6 h-6" />
                        </Tooltip>
                      </button>
                    </div>
                  }
                />
              );
            })}
          </div>
        </div>
      </div>

      <PocModal
        setOpenModalForPoc={setOpenModalForPoc}
        updatePoc={updatePoc}
        instantPublish={post.instant_publish}
        deletePoc={deletePoc}
        defaultDate={post?.scheduled_at || undefined}
        postOnChannel={openModalForPoc}
        validationObject={validationObject}
        hChannelAccounts={hChannelAccounts}
        hBodies={hBodies}
        disabledDate={disabledDate}
      />
    </>
  );
}


const PocModal = ({
  hChannelAccounts,
  validationObject,
  // errors: globalErrors,
  postOnChannel,
  updatePoc,
  defaultDate,
  instantPublish,
  deletePoc,
  setOpenModalForPoc,
  hBodies,
  disabledDate,
}: {
  instantPublish: boolean;
  postOnChannel: ProvisionalPost["post_on_channels_attributes"][0];
  hChannelAccounts: Record<number, ChannelAccount>;
  validationObject: ValidationObject;
  defaultDate?: string;
  updatePoc: (
    poc: ProvisionalPost["post_on_channels_attributes"][0],
    attributes: Record<string, any>
  ) => void;
  deletePoc: (poc: ProvisionalPost["post_on_channels_attributes"][0]) => void;
  setOpenModalForPoc: React.Dispatch<
    React.SetStateAction<
      ProvisionalPost["post_on_channels_attributes"][0] | null
    >
  >;
  hBodies: Record<number, ProvisionalBody>;
  disabledDate: boolean;
}) => {
  const intl = useIntl();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    postOnChannel && setOpen(true);
  }, [postOnChannel]);

  useEffect(() => {
    !open && setTimeout(() => setOpenModalForPoc(null), 250);
  }, [open]);

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const channelAccount =
    postOnChannel && hChannelAccounts[postOnChannel.channel_account_id];
  const { errors, reverseMap } = validationObject;

  const pocErrors =
    reverseMap[channelAccount?.id || 0]?.map(
      (identifier) => errors[identifier]
    ) || [];

  const forcedScheduledAt = postOnChannel?.forced_scheduled_at || defaultDate;
  const fmtForcedScheduledAt = forcedScheduledAt
    ? new Date(forcedScheduledAt).toLocaleString(intl.locale)
    : undefined;

  const identifier = channelAccount?.channel?.identifier;
  const dateDisabled = instantPublish || disabledDate;
  const askDelete = () => setIsConfirmModalOpen(true);

  return (
    <>
      <Modal open={open} setOpen={setOpen}>
        <div className="relative flex flex-col w-[32rem] max-w-full max-h-[calc(100dvh-2rem)] m-auto bg-white border rounded-lg shadow">
          <XMarkIcon
            className="absolute z-10 bg-white border rounded cursor-pointer size-6 top-1 right-1 hover:bg-gray-50"
            onClick={() => setOpen(false)}
          />
          <div className="flex flex-col px-8 py-6 overflow-y-auto [&>*]:shrink-0">
            <div className="flex flex-col gap-3 pb-4">
              <div className="flex items-center gap-3">
                <div className="relative -ml-2">
                  <ChannelAccountImage
                    identifier={identifier as Channel["identifier"]}
                    ca={channelAccount}
                    withOutline={true}
                    isActive={true}
                    imageSize={10}
                    logoSize={4}
                    withChannelLogo={true}
                  />
                </div>
                <p className="line-clamp-2 text-start">
                  {channelAccount?.displayed_name}
                </p>
              </div>
              {pocErrors.length > 0 && (
                <ul className="flex flex-col gap-1 text-red-500">
                  {pocErrors.map((error, idx) => (
                    <li key={idx} className="flex gap-3 text-start">
                      <ExclamationCircleIcon className="size-6 shrink-0" />
                      {/* les messages de usePubValidation interpolent déjà le canal */}
                      {error?.message}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <OptionGroup>
              <OptionRow
                title={intl.formatMessage({ id: "publication_manager.customize_date", defaultMessage: "Personnaliser la date" })}
                hint={
                  dateDisabled
                    ? intl.formatMessage({ id: "publication_manager.must_schedule", defaultMessage: "Vous devez programmer la publication pour pouvoir sélectionner une date." })
                    : undefined
                }
                control={
                  <Flatpickr
                    defaultValue={fmtForcedScheduledAt}
                    name={`datetime-${postOnChannel?.id}`}
                    onChange={(dateArray: Date[]) =>
                      updatePoc(postOnChannel, {
                        forced_scheduled_at: dateArray[0].toISOString(),
                      })
                    }
                    disabled={dateDisabled}
                    className={`w-48 shrink-0 text-sm border-gray-300 rounded-md shadow-sm form-input focus:border-brand_focus focus:ring focus:ring-brand_focus focus:ring-opacity-20 ${dateDisabled && "bg-gray-200"}`}
                    style={{ cursor: dateDisabled ? "default" : "pointer" }}
                    options={{
                      enableTime: true,
                      time_24hr: true,
                      dateFormat: "d/m/Y H:i:S",
                      minDate: new Date(),
                    }}
                  />
                }
              />
            </OptionGroup>

            {identifier === "facebook" && (
              <FacebookPubOptions
                postOnChannel={postOnChannel}
                channelAccount={channelAccount}
                updatePoc={updatePoc}
              />
            )}
            {identifier === "instagram" && (
              <InstagramPubOptions
                postOnChannel={postOnChannel}
                updatePoc={updatePoc}
              />
            )}
            {identifier === "youtube" && (
              <YoutubePubOptions
                postOnChannel={postOnChannel}
                updatePoc={updatePoc}
              />
            )}
            {/* TikTok n'enregistre qu'à la validation : il porte son propre pied de modale */}
            {identifier === "tiktok" ? (
              <TiktokPubOptions
                postOnChannel={postOnChannel}
                channelAccount={channelAccount}
                updatePoc={updatePoc}
                setOpen={setOpen}
                onDelete={askDelete}
              />
            ) : (
              <PocFooter onDelete={askDelete}>
                {/* les options s'enregistrent au fil de l'eau : le bouton ferme simplement la modale */}
                <button
                  type="button"
                  className="btn-brand-primary"
                  onClick={() => setOpen(false)}
                >
                  {intl.formatMessage({ id: "publication_manager.save", defaultMessage: "Enregistrer" })}
                </button>
              </PocFooter>
            )}
          </div>
        </div>
      </Modal>

      {isConfirmModalOpen && (
        <Modal open={isConfirmModalOpen} setOpen={setIsConfirmModalOpen}>
          <div className="p-6 m-auto bg-white rounded-lg">
            <h2 className="mb-4 text-xl font-semibold">
              {intl.formatMessage({ id: "publication_manager.confirm_delete", defaultMessage: "Confirmer la suppression" })}
            </h2>
            <p className="mb-6">{intl.formatMessage({ id: "publication_manager.confirm_delete_msg", defaultMessage: "Êtes-vous sûr de vouloir supprimer ce post ?" })}</p>
            <div className="flex justify-end gap-4">
              <button
                className="px-4 py-2 text-gray-800 duration-200 bg-gray-200 rounded-md hover:bg-gray-100"
                onClick={() => setIsConfirmModalOpen(false)}
              >
                {intl.formatMessage({ id: "publication_manager.cancel", defaultMessage: "Annuler" })}
              </button>
              <button
                className="px-4 py-2 text-white duration-200 bg-red-500 rounded-md hover:bg-red-700"
                onClick={() => {
                  deletePoc(postOnChannel);
                  setOpen(false); // Close the first modal as well
                  setIsConfirmModalOpen(false);
                }}
              >
                {intl.formatMessage({ id: "publication_manager.delete", defaultMessage: "Supprimer" })}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

type UpdatePoc = (
  poc: ProvisionalPost["post_on_channels_attributes"][0],
  attributes: Record<string, any>
) => void;

const inputClass = "w-full text-sm form-input rounded-md border-gray-300 shadow-sm focus:border-brand_focus focus:ring focus:ring-brand_focus focus:ring-opacity-20";

const Toggle = ({
  checked,
  onChange,
  label,
  disabled = false,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: string;
  disabled?: boolean;
}) => (
  <Switch
    checked={checked}
    onChange={onChange}
    disabled={disabled}
    className={`${checked ? "bg-green-300" : "bg-gray-200"} ${disabled && "opacity-50 cursor-not-allowed"}
      relative inline-flex h-6 w-11 min-w-11 items-center rounded-full focus:ring-4 focus:outline-none ring-brand_focus ring-opacity-50 focus:border focus:border-brand_focus`}
  >
    <span className="sr-only">{label}</span>
    <span className={`${checked ? "translate-x-6" : "translate-x-1"} inline-block h-4 w-4 transform rounded-full bg-white transition`} />
  </Switch>
);

// Une ligne d'option : titre + aide à gauche, contrôle à droite, détails éventuels en dessous.
const OptionRow = ({
  title,
  hint,
  control,
  children,
}: {
  title: React.ReactNode;
  hint?: React.ReactNode;
  control?: React.ReactNode;
  children?: React.ReactNode;
}) => (
  <div className="flex flex-col gap-2 text-start">
    <div className="flex items-center justify-between gap-4">
      {/* moitié de la largeur : l'aide passe à la ligne au lieu de filer jusqu'au contrôle */}
      <div className="flex flex-col w-1/2 gap-0.5">
        <span className="text-sm font-semibold text-gray-700">{title}</span>
        {hint && <span className="text-xs text-gray-500">{hint}</span>}
      </div>
      {control}
    </div>
    {children}
  </div>
);

// Une section de la modale : les sections sont séparées par un trait (divide-y du conteneur),
// les options d'une même section ne sont qu'espacées.
const OptionGroup = ({
  title,
  children,
}: {
  title?: string;
  children: React.ReactNode;
}) => (
  <div className="flex flex-col gap-5 py-5">
    {title && (
      <p className="text-base font-bold text-gray-900 text-start">
        {title}
      </p>
    )}
    {children}
  </div>
);

const PocFooter = ({
  onDelete,
  children,
}: {
  onDelete: () => void;
  children?: React.ReactNode;
}) => {
  const intl = useIntl();

  return (
    <div className="flex items-center justify-between gap-4 pt-4">
      <button
        type="button"
        className="inline-flex items-center gap-1 text-sm font-medium text-red-600 duration-100 hover:text-red-800"
        onClick={onDelete}
      >
        <TrashIcon className="size-4" />
        {intl.formatMessage({ id: "publication_manager.delete_post", defaultMessage: "Supprimer ce post" })}
      </button>
      {children}
    </div>
  );
};

// Premier commentaire et automatisation DM vont ensemble : le commentaire annonce le mot-clé
// auquel l'automatisation répond.
const useCommentAutomation = (
  postOnChannel: ProvisionalPost["post_on_channels_attributes"][0]
) => {
  const data = postOnChannel.channel_specific_data;
  const [firstComment, setFirstComment] = useState<string>(data?.first_comment || "");
  const [dmEnabled, setDmEnabled] = useState<boolean>(data?.dm_automation?.enabled || false);
  const [dmKeyword, setDmKeyword] = useState<string>(data?.dm_automation?.message || "");
  const [dmResponse, setDmResponse] = useState<string>(data?.dm_automation?.response || "");

  return {
    state: { firstComment, dmEnabled, dmKeyword, dmResponse },
    setters: { setFirstComment, setDmEnabled, setDmKeyword, setDmResponse },
    data: {
      first_comment: firstComment,
      dm_automation: { enabled: dmEnabled, message: dmKeyword, response: dmResponse },
    },
  };
};

const CommentAutomationGroup = ({
  automation,
  messagePrefix,
}: {
  automation: ReturnType<typeof useCommentAutomation>;
  messagePrefix: "facebook_options" | "instagram_options";
}) => {
  const intl = useIntl();
  const { firstComment, dmEnabled, dmKeyword, dmResponse } = automation.state;
  const { setFirstComment, setDmEnabled, setDmKeyword, setDmResponse } = automation.setters;
  const isFacebook = messagePrefix === "facebook_options";

  return (
    <>
      <OptionRow
        title={intl.formatMessage({ id: `${messagePrefix}.first_comment`, defaultMessage: "Premier commentaire" })}
      >
        <textarea
          value={firstComment}
          onChange={(e) => setFirstComment(e.currentTarget.value)}
          rows={2}
          className={inputClass}
          placeholder={
            isFacebook
              ? intl.formatMessage({ id: "facebook_options.first_comment_placeholder", defaultMessage: "Texte du premier commentaire..." })
              : intl.formatMessage({ id: "instagram_options.first_comment_placeholder", defaultMessage: "Commente LIEN pour recevoir le lien 🔑" })
          }
        />
      </OptionRow>
      <OptionRow
        title={intl.formatMessage({ id: `${messagePrefix}.dm_automation`, defaultMessage: "Automatisation des DM" })}
        hint={
          isFacebook
            ? intl.formatMessage({ id: "facebook_options.dm_automation_hint", defaultMessage: "Si quelqu'un commente le mot-clé, il reçoit automatiquement le message en Messenger." })
            : intl.formatMessage({ id: "instagram_options.dm_automation_hint", defaultMessage: "Si un abonné commente le mot-clé, il reçoit automatiquement le message en DM." })
        }
        control={
          <Toggle
            checked={dmEnabled}
            onChange={setDmEnabled}
            label={intl.formatMessage({ id: `${messagePrefix}.dm_automation`, defaultMessage: "Automatisation des DM" })}
          />
        }
      >
        {dmEnabled && (
          <>
            <input
              type="text"
              value={dmKeyword}
              onChange={(e) => setDmKeyword(e.currentTarget.value)}
              className={inputClass}
              placeholder={intl.formatMessage({ id: `${messagePrefix}.dm_keyword_placeholder`, defaultMessage: "Mot-clé (ex: LIEN)" })}
            />
            <input
              type="text"
              value={dmResponse}
              onChange={(e) => setDmResponse(e.currentTarget.value)}
              className={inputClass}
              placeholder={intl.formatMessage({ id: `${messagePrefix}.dm_response_placeholder`, defaultMessage: "Message à envoyer (ex: https://...)" })}
            />
          </>
        )}
      </OptionRow>
    </>
  );
};

const FacebookPubOptions = ({
  postOnChannel,
  channelAccount,
  updatePoc,
}: {
  postOnChannel: ProvisionalPost["post_on_channels_attributes"][0];
  channelAccount: ChannelAccount;
  updatePoc: UpdatePoc;
}) => {
  const intl = useIntl();
  const missingScopes = channelAccount.missing_scopes_for_comment_automation ?? [];
  const available = missingScopes.length === 0;
  const automation = useCommentAutomation(postOnChannel);

  useEffect(() => {
    if (!available) return;
    updatePoc(postOnChannel, {
      channel_specific_data: {
        ...postOnChannel.channel_specific_data,
        ...automation.data,
      },
    });
  }, Object.values(automation.state));

  return (
    <OptionGroup title={intl.formatMessage({ id: "publication_manager.group_comments", defaultMessage: "Commentaires & DM" })}>
      {available ? (
        <CommentAutomationGroup automation={automation} messagePrefix="facebook_options" />
      ) : (
        <p className="text-sm italic text-gray-500 text-start">
          {intl.formatMessage({ id: "missing_scopes.reconnect_to_enable", defaultMessage: "Reconnectez votre compte pour activer cette fonctionnalité." })}
        </p>
      )}
    </OptionGroup>
  );
};

const TiktokPubOptions = ({
  postOnChannel,
  channelAccount,
  updatePoc,
  setOpen,
  onDelete,
}: {
  postOnChannel: ProvisionalPost["post_on_channels_attributes"][0];
  channelAccount: ChannelAccount;
  updatePoc: UpdatePoc;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onDelete: () => void;
}) => {
  const intl = useIntl();
  const [settings, setSettings] = useState<Record<string, any>>({
    ...postOnChannel?.channel_specific_data,
  });

  const [isSwitchOn, setIsSwitchOn] = useState(false);
  const [errors, setErrors] = useState<Record<string, string | null>>({});

  useEffect(() => {
    const hasValue = Boolean(settings?.privacy_level);
    setErrors((prev) => ({
      ...prev,
      privacy_level: hasValue ? null : intl.formatMessage({ id: "tiktok_options.select_visibility_error", defaultMessage: "Sélectionnez un niveau de visibilité" }),
    }));
  }, [settings?.privacy_level]);

  useEffect(() => {
    const error =
      isSwitchOn &&
        !settings.brand_organic_toggle &&
        !settings?.brand_content_toggle
        ? intl.formatMessage({ id: "tiktok_options.select_commercial_error", defaultMessage: "Sélectionnez un type de contenu commercial" })
        : null;
    setErrors((prev) => ({ ...prev, commercial_content: error }));
  }, [
    settings.brand_organic_toggle,
    settings.brand_content_toggle,
    isSwitchOn,
  ]);

  useEffect(() => {
    if (settings.brand_content_toggle) {
      setVisibilityOptions((prev) => [
        ...prev.filter((option) => option.value !== "SELF_ONLY"),
        { label: intl.formatMessage({ id: "tiktok_options.private", defaultMessage: "Privée" }), value: "SELF_ONLY", disabled: true },
      ]);
    } else {
      setVisibilityOptions((prev) => [
        ...prev.filter((option) => option.value !== "SELF_ONLY"),
        { label: intl.formatMessage({ id: "tiktok_options.private", defaultMessage: "Privée" }), value: "SELF_ONLY" },
      ]);
    }
  }, [settings.brand_content_toggle]);

  const updateSettings = (attributes: Record<string, any>) => {
    setSettings((prev) => ({
      ...prev,
      ...attributes,
    }));
  };

  const toggleSwitch = (value: boolean) => {
    setIsSwitchOn(value);
    if (!value) {
      updateSettings({
        brand_organic_toggle: false,
        brand_content_toggle: false,
      });
    }
  };

  const visibilityMapping = {
    PUBLIC_TO_EVERYONE: intl.formatMessage({ id: "tiktok_options.public", defaultMessage: "Publique" }),
    SELF_ONLY: intl.formatMessage({ id: "tiktok_options.private", defaultMessage: "Privée" }),
    MUTUAL_FOLLOW_FRIENDS: intl.formatMessage({ id: "tiktok_options.friends", defaultMessage: "Amis" }),
    FOLLOWER_OF_CREATOR: "Followers",
  };

  // privacy_level_options vient du creator_info TikTok via sync_profile : absent en dev
  // (prepare_for_post! ne synchronise pas) et si la synchro a échoué. On retombe alors sur
  // les visibilités communes à tous les types de compte (FOLLOWER_OF_CREATOR est réservé
  // aux comptes business et n'est proposé que si TikTok le renvoie).
  const DEFAULT_PRIVACY_LEVELS: (keyof typeof visibilityMapping)[] = [
    "PUBLIC_TO_EVERYONE",
    "MUTUAL_FOLLOW_FRIENDS",
    "SELF_ONLY",
  ];

  const [visibilityOptions, setVisibilityOptions] = useState<
    { label: string; value: keyof typeof visibilityMapping }[]
  >(
    (
      (channelAccount.channel_specific_data?.privacy_level_options as
        | (keyof typeof visibilityMapping)[]
        | undefined) ?? DEFAULT_PRIVACY_LEVELS
    ).map((option) => ({
      label: visibilityMapping[option],
      value: option,
    }))
  );

  const [isMusicModalOpen, setIsMusicModalOpen] = useState(false);

  const isErrored = Object.values(errors).some((e) => e !== null);

  const interactions = [
    { key: "disable_comments", id: "tiktok_options.allow_comments", defaultMessage: "Autoriser les commentaires" },
    { key: "disable_duet", id: "tiktok_options.allow_duets", defaultMessage: "Autoriser les duos" },
    { key: "disable_stitch", id: "tiktok_options.allow_stitches", defaultMessage: "Autoriser les collages" },
  ];

  const musicLink = (
    <span
      className="italic text-indigo-500 cursor-pointer"
      onClick={() => setIsMusicModalOpen(true)}
    >
      {intl.formatMessage({ id: "tiktok_options.music_confirmation", defaultMessage: "Confirmation d'utilisation de la musique" })}
    </span>
  );

  return (
    <>
      <OptionGroup>
        <OptionRow
          title={intl.formatMessage({ id: "tiktok_options.visibility", defaultMessage: "Visibilité" })}
          control={
            <div className="w-48 shrink-0">
              <Select
                value={settings?.privacy_level}
                placeHolder={intl.formatMessage({ id: "tiktok_options.select_visibility", defaultMessage: "Sélectionner la visibilité" })}
                list={visibilityOptions}
                onChange={(value) => updateSettings({ privacy_level: value })}
                error={errors.privacy_level}
              />
            </div>
          }
        />
      </OptionGroup>

      <OptionGroup title={intl.formatMessage({ id: "publication_manager.group_interactions", defaultMessage: "Interactions" })}>
        {interactions.map(({ key, id, defaultMessage }) => {
          const label = intl.formatMessage({ id, defaultMessage });
          return (
            <OptionRow
              key={key}
              title={label}
              control={
                <Toggle
                  checked={!settings?.[key]}
                  onChange={(e) => updateSettings({ [key]: !e })}
                  label={label}
                />
              }
            />
          );
        })}
      </OptionGroup>

      <OptionGroup>
        <OptionRow
          title={intl.formatMessage({ id: "tiktok_options.commercial_content", defaultMessage: "Contenu commercial" })}
          hint={intl.formatMessage({ id: "tiktok_options.commercial_desc", defaultMessage: "Indique aux autres utilisateurs que cette publication fait la promotion d'une marque, d'un produit ou d'un service." })}
          control={<Toggle checked={isSwitchOn} onChange={toggleSwitch} label="Contenu sponsorisé" />}
        >
          {(isSwitchOn ||
            settings?.brand_organic_toggle ||
            settings?.brand_content_toggle) && (
              <div className="flex flex-col gap-3 pl-4 border-l-2 border-gray-100">
                <div className="flex justify-between gap-3">
                  <Tooltip
                    text={intl.formatMessage({ id: "tiktok_options.promotional_tooltip", defaultMessage: "Votre photo/vidéo sera étiquetée comme 'Contenu promotionnel'" })}
                    position="left"
                  >
                    {intl.formatMessage({ id: "tiktok_options.my_brand", defaultMessage: "Pour ma marque" })}
                  </Tooltip>
                  <Toggle
                    checked={settings?.brand_organic_toggle}
                    onChange={(e) => updateSettings({ brand_organic_toggle: e })}
                    label="Pour ma marque"
                  />
                </div>
                <div
                  className={`flex gap-3 justify-between ${settings?.privacy_level === "SELF_ONLY" && "text-gray-300"}`}
                >
                  <Tooltip
                    text={intl.formatMessage({ id: "tiktok_options.sponsored_tooltip", defaultMessage: "Votre photo/vidéo sera étiquetée comme 'Contenu sponsorisé'" })}
                    position="left"
                  >
                    {intl.formatMessage({ id: "tiktok_options.other_brand", defaultMessage: "Rémunéré par une autre marque" })}
                  </Tooltip>
                  <Toggle
                    checked={settings?.brand_content_toggle}
                    onChange={(e) => updateSettings({ brand_content_toggle: e })}
                    disabled={settings?.privacy_level === "SELF_ONLY"}
                    label="Pour une autre marque"
                  />
                </div>

                {errors.commercial_content && (
                  <small className="text-sm text-red-500">
                    {errors.commercial_content}
                  </small>
                )}

                {settings?.brand_organic_toggle &&
                  !settings?.brand_content_toggle && (
                    <div className="text-sm text-gray-700">
                      {intl.formatMessage({ id: "tiktok_options.terms_accept", defaultMessage: "En postant ce contenu vous acceptez les conditions d'utilisation de" })}{" "}
                      {musicLink}
                    </div>
                  )}
                {settings?.brand_content_toggle && (
                  <div className="text-sm text-gray-700">
                    {intl.formatMessage({ id: "tiktok_options.terms_accept", defaultMessage: "En postant ce contenu vous acceptez les conditions d'utilisation de" })}{" "}
                    <a
                      href="https://www.tiktok.com/legal/page/global/bc-policy/fr"
                      target="blank"
                      className="italic text-indigo-500"
                    >
                      {intl.formatMessage({ id: "tiktok_options.brand_policy", defaultMessage: "Politique relative au contenu de marque" })}
                    </a>{" "}
                    {intl.formatMessage({ id: "tiktok_options.and_the", defaultMessage: "et la" })}{" "}
                    {musicLink}
                  </div>
                )}
                {isMusicModalOpen && (
                  <Modal open={isMusicModalOpen} setOpen={setIsMusicModalOpen}>
                    <div className="max-w-md p-6 m-auto bg-white rounded-lg">
                      <h2 className="mb-4 text-xl font-semibold">
                        {intl.formatMessage({ id: "tiktok_options.music_confirmation", defaultMessage: "Confirmation d'utilisation de la musique" })}
                      </h2>
                      <p className="mb-6">
                        {intl.formatMessage({ id: "tiktok_options.music_rights_text", defaultMessage: "Tu confirmes (a) qu'il n'y a pas de musique protégée par le droit d'auteur dans la vidéo ou (b) que tu as obtenu et payé toutes les licences nécessaires pour utiliser la musique dans le cadre de cette publication sur la plateforme TikTok. Si tu ne peux pas confirmer (a) ou (b) ci-dessus, alors n'accepte pas. Remarque : si tu partages quand même ta publication, celle-ci pourrait être retirée." })}
                      </p>
                      <div className="flex justify-end gap-4">
                        <button
                          className="btn-brand-primary"
                          onClick={() => setIsMusicModalOpen(false)}
                        >
                          {intl.formatMessage({ id: "tiktok_options.close", defaultMessage: "Fermer" })}
                        </button>
                      </div>
                    </div>
                  </Modal>
                )}
              </div>
            )}
        </OptionRow>
      </OptionGroup>

      <PocFooter onDelete={onDelete}>
        <button
          type="button"
          className={`rounded-md inline-flex items-center px-2.5 py-1.5 font-medium text-sm shadow-sm duration-100 justify-center
            ${!isErrored
              ? "bg-brand_main text-white hover:bg-brand_darker cursor-pointer"
              : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
          disabled={isErrored}
          onClick={() => {
            if (!isErrored) {
              setOpen(false);
              updatePoc(postOnChannel, {
                channel_specific_data: {
                  ...postOnChannel.channel_specific_data,
                  ...settings,
                },
              });
            }
          }}
        >
          {intl.formatMessage({ id: "tiktok_options.validate", defaultMessage: "Valider les paramètres TikTok" })}
        </button>
      </PocFooter>
    </>
  );
};

const InstagramPubOptions = ({
  postOnChannel,
  updatePoc,
}: {
  postOnChannel: ProvisionalPost["post_on_channels_attributes"][0];
  updatePoc: UpdatePoc;
}) => {
  const intl = useIntl();
  const [currentCollab, setCurrentColab] = useState<string>("");

  const [collabs, setCollabs] = useState<string[]>(
    postOnChannel.channel_specific_data?.collaborators || []
  ); // parceque le poc ne se met pas à jour ici

  const [coverUrl, setCoverUrl] = useState<string>(
    postOnChannel.channel_specific_data?.cover_url || ""
  );


  const automation = useCommentAutomation(postOnChannel);

  useEffect(() => {
    updatePoc(postOnChannel, {
      channel_specific_data: {
        ...postOnChannel.channel_specific_data,
        collaborators: collabs,
        cover_url: coverUrl,
        ...automation.data,
      },
    });
  }, [collabs, coverUrl, ...Object.values(automation.state)]);

  const submitCollab = () => {
    if (currentCollab) {
      setCollabs([...collabs, currentCollab]);
    }
    setCurrentColab("");
  };

  const removeCollab = (toRemove: string) => {
    setCollabs(collabs.filter((c) => toRemove != c));
  };

  return (
    <>
      <OptionGroup title={intl.formatMessage({ id: "publication_manager.group_publication", defaultMessage: "Publication" })}>
        <OptionRow
          title={intl.formatMessage({ id: "instagram_options.collaborators", defaultMessage: "Collaborateurs" })}
        >
          {collabs.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {collabs.map((c) => (
                <button
                  key={c}
                  type="button"
                  className="group relative text-sm font-medium text-gray-500 border-gray-300 bg-gray-100 border px-1 py-0.5 rounded-md hover:border-gray-400 hover:bg-gray-200 hover:text-gray-600"
                  onClick={() => removeCollab(c)}
                >
                  {c}
                  <div className="absolute top-0 right-0 hidden h-full overflow-hidden rounded-md group-hover:flex">
                    <div className="pl-2 pr-0.5 bg-gradient-to-r from-transparent to-40% to-gray-200 flex items-center">
                      <XMarkIcon className="size-4" />
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
          <div className="relative flex items-center">
            <input
              type="text"
              value={currentCollab}
              onChange={(e) => setCurrentColab(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  submitCollab();
                }
              }}
              className={`peer pr-16 ${inputClass}`}
              placeholder="squeezie"
            />
            <button
              type="button"
              onClick={submitCollab}
              className="absolute right-0 flex items-center w-8 h-full p-1 font-medium text-gray-500 duration-300 border border-gray-300 peer-focus:border-brand_focus peer-focus:text-brand_main bg-gray-50 rounded-r-md hover:bg-gray-100"
            >
              <PlusIcon className="inline w-4 h-4 m-auto" />
            </button>
          </div>
        </OptionRow>

        {/* La cover ne sert qu'à un reel : le serveur l'ignore pour un post d'images. */}
        <OptionRow
          title={intl.formatMessage({ id: "instagram_options.cover", defaultMessage: "Ajouter une cover" })}
        >
          <CoverUploader onUploaded={(url) => url && setCoverUrl(url)} />
          {coverUrl && (
            <span
              className="relative self-start cursor-pointer group"
              onClick={() => setCoverUrl("")}
            >
              <img src={coverUrl} alt="Aperçu de la cover" className="block object-cover w-auto h-32 rounded-md" />
              <div className="absolute items-center justify-center hidden p-1 rounded-full shadow top-1 right-1 group-hover:flex bg-white/90">
                <XMarkIcon className="text-gray-700 size-4" />
              </div>
            </span>
          )}
        </OptionRow>
      </OptionGroup>

      <OptionGroup title={intl.formatMessage({ id: "publication_manager.group_comments", defaultMessage: "Commentaires & DM" })}>
        <CommentAutomationGroup automation={automation} messagePrefix="instagram_options" />
      </OptionGroup>
    </>
  );
};

const YoutubePubOptions = ({
  postOnChannel,
  updatePoc,
}: {
  postOnChannel: ProvisionalPost["post_on_channels_attributes"][0];
  updatePoc: UpdatePoc;
}) => {
  const intl = useIntl();
  const [videoTitle, setVideoTitle] = useState<string>(
    postOnChannel.channel_specific_data?.video_title || ""
  );
  const [privacyStatus, setPrivacyStatus] = useState<string>(
    postOnChannel.channel_specific_data?.privacy_status || "public"
  );

  useEffect(() => {
    updatePoc(postOnChannel, {
      channel_specific_data: {
        ...postOnChannel.channel_specific_data,
        video_title: videoTitle,
        privacy_status: privacyStatus,
      },
    });
  }, [videoTitle, privacyStatus]);

  return (
    <OptionGroup>
      <OptionRow
        title={
          <label htmlFor="youtube-video-title">
            {intl.formatMessage({ id: "youtube_options.video_title", defaultMessage: "Titre de la vidéo" })}
          </label>
        }
        control={
          <span className="text-xs text-gray-500">
            {intl.formatMessage({ id: "youtube_options.characters_count", defaultMessage: "{count}/100 caractères" }, { count: videoTitle.length })}
          </span>
        }
      >
        <input
          id="youtube-video-title"
          type="text"
          value={videoTitle}
          maxLength={100}
          onChange={(e) => setVideoTitle(e.currentTarget.value)}
          className={inputClass}
          placeholder={intl.formatMessage({ id: "youtube_options.video_title_placeholder", defaultMessage: "Titre de votre vidéo YouTube" })}
        />
      </OptionRow>

      <OptionRow
        title={
          <label htmlFor="youtube-privacy-status">
            {intl.formatMessage({ id: "youtube_options.privacy_status", defaultMessage: "Visibilité" })}
          </label>
        }
        control={
          <select
            id="youtube-privacy-status"
            value={privacyStatus}
            onChange={(e) => setPrivacyStatus(e.currentTarget.value)}
            className="w-48 text-sm border-gray-300 rounded-md shadow-sm shrink-0 form-select focus:border-brand_focus focus:ring focus:ring-opacity-20"
          >
            <option value="public">
              {intl.formatMessage({ id: "youtube_options.privacy_public", defaultMessage: "Publique" })}
            </option>
            <option value="unlisted">
              {intl.formatMessage({ id: "youtube_options.privacy_unlisted", defaultMessage: "Non répertoriée" })}
            </option>
            <option value="private">
              {intl.formatMessage({ id: "youtube_options.privacy_private", defaultMessage: "Privée" })}
            </option>
          </select>
        }
      />
    </OptionGroup>
  );
};
